import React, { useEffect, useState } from "react";

import { CloseGreyIcon, CrossRed } from "../../../assets/icons";
import AddFunctionalityView from "./AddPlanFeatureView";
import { GetPlanFeatureResponseDTO } from "../../../models/planFeature/planFeture.response";
import { planFeatureService } from "../../../services/planFeatureService";
import { useNotification } from "../../toster-notification/NotificationContext";
import Loader from "../../shared/Loader";
import { planService } from "../../../services/planService";
import { PlanDetailsDTO } from "../../../models/plan/plan.response";

interface EditPlanViewProps {
    planId: string;
    onClose: () => void;
}

const EditPlanView: React.FC<EditPlanViewProps> = ({ planId, onClose }) => {
    const [isAddFunctionalityModalOpen, setIsAddFunctionalityModalOpen] = useState(false);

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");
    const [price, setPrice] = useState<number>();
    const [priceError, setPriceError] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [descriptionError, setDescriptionError] = useState<string>("");
    const [duration, setDuration] = useState<number>();
    const [durationError, setDurationError] = useState<string>("");
    const [selectedFunctionalities, setSelectedFunctionalities] = useState<GetPlanFeatureResponseDTO[]>([]);

    const [plan, setPlan] = useState<PlanDetailsDTO>();
    const [functionalities, setFunctionalities] = useState<GetPlanFeatureResponseDTO[]>([]);
    const [functionalitiesError, setFunctionalitiesError] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);

    const { notify } = useNotification();

    useEffect(() => {
        fetchFunctionalities();
        fetchPlan(planId);
    }, []);

    const handleUpdatePlan = async () => {

        setNameError("");
        setPriceError("");
        setDurationError("");
        setFunctionalitiesError("");

        let isValid = true;

        if (name.trim() === "") {
            setNameError("Name is required.");
            isValid = false;
        }

        if (price === undefined || price <= 0) {
            setPriceError("Price must be greater than zero.");
            isValid = false;
        }

        if (duration === undefined || duration <= 0) {
            setDurationError("Duration must be greater than zero.");
            isValid = false;
        }

        if (!isValid) {
            return;
        }

        setLoading(true);
        try {
            const response = await planService.update({
                id: planId,
                name: name,
                description: description,
                price: price || 0,
                durationDays: duration || 0,
                planFeatures: selectedFunctionalities.map(f => f.id)
            });

            if (response.success && response.message) {
                notify("success", response.message);
                onClose();
            }

        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to update plan.");
            }
        } finally {
            setLoading(false);
        }
    };


    const fetchFunctionalities = async () => {
        setLoading(true);

        try {
            const response = await planFeatureService.getPlanFeatures();
            if (response.success && response.data) {
                setFunctionalities(response.data);
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Došlo je do greške prilikom učitavanja funkcionalnosti.");
            }

        } finally {
            setLoading(false);
        }
    };

    const fetchPlan = async (planId: string) => {
        setLoading(true);

        try {
            const response = await planService.getPlan(planId);
            if (response.success && response.data) {
                setPlan(response.data);
                setName(response.data.name);
                setDescription(response.data.description || "");
                setPrice(response.data.price);
                setDuration(response.data.durationDays);
                setSelectedFunctionalities(response.data.planFeatures || []);
            }
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.message) {
                notify("error", error.response.data.message);
            } else {
                notify("error", "Failed to fetch plan details.");
            }

        } finally {
            setLoading(false);
        }
    };

    const onSelectFunctionality = (func: GetPlanFeatureResponseDTO) => {
        if (!selectedFunctionalities.some(f => f.id === func.id)) {
            setSelectedFunctionalities([...selectedFunctionalities, func])
        } else {
            setSelectedFunctionalities(selectedFunctionalities.filter(f => f.id !== func.id))
        }
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {isAddFunctionalityModalOpen && (
                <AddFunctionalityView
                    onClose={() => {
                        setIsAddFunctionalityModalOpen(false)
                        fetchFunctionalities();
                    }} />
            )}

            <section className="modal-section plan">
                <div className="w-layout-blockcontainer container w-container">
                    <div className="modal-wrapper">
                        <div className="modal-div">
                            <div className="modal-header-div">
                                <div>
                                    <div className="modal-header">
                                        <strong>Novi Plan</strong>
                                    </div>
                                    <div className="description-text">Kreirajte novi plan pretplate</div>
                                </div>
                                <div className="modal-button-div" onClick={onClose}>
                                    <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                                </div>
                            </div>
                            <div className="modal-form-block w-form">
                                <form className="modal-form">

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Naziv Plana *</label>
                                        <input className="form-input grey w-input" placeholder="" type="text" required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                        <div className="error-text margin-top--5px">{nameError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Opis </label>
                                        <textarea placeholder="" className="form-textarea grey w-input"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                        ></textarea>
                                        <div className="error-text margin-top--5px">{descriptionError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Cena (RSD) *</label>
                                        <input className="form-input grey w-input" placeholder="" type="number" required
                                            value={price}
                                            onChange={(e) => setPrice(Number(e.target.value) > 0 ? Number(e.target.value) : 0)}
                                        />
                                        <div className="error-text margin-top--5px">{priceError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <label className="modal-form-label">Trajanje (dana) *</label>
                                        <input className="form-input grey w-input" type="number" placeholder="" required
                                            value={duration}
                                            onChange={(e) => setDuration(Number(e.target.value) > 0 ? Number(e.target.value) : 0)}
                                        />
                                        <div className="error-text margin-top--5px">{durationError}</div>
                                    </div>

                                    <div className="modal-form-div">
                                        <div className="div-block-30">
                                            <label className="modal-form-label">Funkcionalnosti</label>
                                            <div className="small-box-div pointer" onClick={() => setIsAddFunctionalityModalOpen(true)}>
                                                <div>Dodaj novu</div>
                                            </div>
                                        </div>
                                        <div className="div-block-31">

                                            {functionalities.map((func) => (
                                                <div key={func.id} className={`small-box-div ${selectedFunctionalities.some(f => f.id === func.id) ? 'green-bg' : 'grey-bg'} pointer`} onClick={() => onSelectFunctionality(func)}>
                                                    <div>{func.name}</div>
                                                    <div className="fs-0-75">{"{"}{func.key || "N/A"}{"}"}</div>
                                                </div>
                                            ))}

                                        </div>
                                        <div className="error-text margin-top--5px">{functionalitiesError}</div>
                                    </div>

                                </form>
                            </div>
                            <div className="modal-buttons">
                                <div className="button-secondary medium" onClick={onClose}>
                                    <div>Otkaži</div>
                                </div>
                                <div className="button-primary medium" onClick={handleUpdatePlan}>
                                    <div>Sačuvaj izmene</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default EditPlanView;